import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import { settleOnboarding } from '../lib/onboardingStore';
import { isTimeoutError, withTimeout } from '../lib/withTimeout';

const AUTH_OPERATION_TIMEOUT_MS = 8000;

type AuthContextType = {
  user: User | null;
  loading: boolean;
  profileComplete: boolean;
  profileCheckSettled: boolean;
  signOut: () => Promise<void>;
  refreshProfileStatus: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileComplete, setProfileComplete] = useState(false);
  const [profileCheckSettled, setProfileCheckSettled] = useState(false);
  const checkProfileCompleteRef = useRef<(user: User) => Promise<void>>(async () => {});

  const attachSettledSession = (sessionUser: User, stillActive: () => boolean) => {
    void (async () => {
      let settled = false;
      try {
        settled = await withTimeout(
          settleOnboarding(sessionUser),
          AUTH_OPERATION_TIMEOUT_MS,
          'Onboarding settle'
        );
      } catch (error) {
        console.error('Could not attach guest progress.', error);
      }
      if (!stillActive()) return;
      await checkProfileCompleteRef.current(sessionUser);
      if (settled && stillActive()) setProfileComplete(true);
    })();
  };

  const finishProfileCheck = (complete: boolean) => {
    setProfileComplete(complete);
    setProfileCheckSettled(true);
  };

  const releaseAuthLoading = () => {
    setLoading(false);
  };

  const clearSessionAndGoHome = async () => {
    setUser(null);
    setProfileComplete(false);
    setProfileCheckSettled(true);
    releaseAuthLoading();
    try {
      await withTimeout(supabase.auth.signOut(), AUTH_OPERATION_TIMEOUT_MS, 'Sign out');
    } catch (signOutError) {
      console.error('Sign out failed:', signOutError);
    }
    // A failed or stale session must not bounce visitors off the public help page.
    if (window.location.pathname !== '/help') {
      window.location.href = '/';
    }
  };

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setUser(null);
      setProfileComplete(false);
      releaseAuthLoading();
      return;
    }

    let active = true;

    // Get initial session with error handling. getSession waits on an auth
    // lock with no timeout; a stalled init or a callback that re-enters that
    // lock would otherwise leave `loading` true forever.
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_OPERATION_TIMEOUT_MS,
          'Auth session check'
        );

        if (!active) return;
        
        if (error) {
          console.error('Error getting initial session:', error);
          
          // Handle specific refresh token errors
          if (error.message?.includes('Refresh Token Not Found') || 
              error.message?.includes('invalid_grant') ||
              error.message?.includes('refresh_token_not_found')) {
            console.log('Invalid refresh token detected, clearing session');
            await clearSessionAndGoHome();
            return;
          }
          
          // Clear any invalid session and redirect to clean state
          await clearSessionAndGoHome();
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          attachSettledSession(session.user, () => active);
        } else {
          releaseAuthLoading();
        }
      } catch (error) {
        if (!active) return;

        if (isTimeoutError(error)) {
          console.error('Auth session check timed out; continuing without blocking the page.');
          releaseAuthLoading();
          return;
        }

        console.error('Unexpected error getting initial session:', error);
        
        // Handle refresh token errors in catch block as well
        if (error instanceof Error && 
            (error.message?.includes('Refresh Token Not Found') || 
             error.message?.includes('invalid_grant') ||
             error.message?.includes('refresh_token_not_found'))) {
          console.log('Invalid refresh token detected in catch, clearing session');
          await clearSessionAndGoHome();
          return;
        }
        
        // Clear any invalid session and redirect to clean state
        await clearSessionAndGoHome();
      }
    };

    void getInitialSession();

    // The auth client invokes this callback while it holds its lock and
    // awaits the returned promise. Defer any Supabase calls so a profile
    // read or sign-out cannot deadlock getSession().
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      window.setTimeout(() => {
        if (!active) return;

        if (event === 'TOKEN_REFRESHED' && !session) {
          console.log('Token refresh failed, clearing session');
          void clearSessionAndGoHome();
          return;
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          attachSettledSession(session.user, () => active);
        } else {
          setProfileComplete(false);
          setProfileCheckSettled(true);
          releaseAuthLoading();
        }
      }, 0);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const checkProfileComplete = async (user: User) => {
    try {
      // Method 1: Check user metadata (fastest, no database query needed)
      const userMetadata = user.user_metadata;
      const appMetadata = user.app_metadata;
      
      // Check if user has completed onboarding flag in metadata
      if (userMetadata?.profile_complete === true || appMetadata?.profile_complete === true) {
        finishProfileCheck(true);
        setLoading(false);
        return;
      }

      // Method 2: Check user creation time vs last sign in (indicates returning user)
      const createdAt = new Date(user.created_at);
      const lastSignIn = user.last_sign_in_at ? new Date(user.last_sign_in_at) : null;
      
      // If user was created more than 5 minutes ago and has signed in before, 
      // they're likely a returning user
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      const isReturningUser = createdAt < fiveMinutesAgo && lastSignIn && lastSignIn > createdAt;

      if (isReturningUser) {
        // For returning users, do a lightweight database check
        const { data, error } = await withTimeout(
          supabase
            .from('profiles')
            .select('first_name')
            .eq('id', user.id)
            .maybeSingle(),
          AUTH_OPERATION_TIMEOUT_MS,
          'Profile check'
        );

        // If profile doesn't exist, create it
        if (error || !data) {
          console.log('Profile not found, creating one...');
          await withTimeout(
            supabase.rpc('create_user_profile', {
              user_id: user.id,
              user_email: user.email,
              user_full_name: user.user_metadata?.full_name || null
            }),
            AUTH_OPERATION_TIMEOUT_MS,
            'Profile create'
          );
        }

        if (error) {
          console.error('Error checking profile:', error);
          
          // Handle refresh token errors
          if (error.message?.includes('Refresh Token Not Found') || 
              error.message?.includes('invalid_grant') ||
              error.message?.includes('refresh_token_not_found')) {
            console.log('Invalid refresh token detected in profile check, clearing session');
            await clearSessionAndGoHome();
            return;
          }
          
          // Check if the error is due to an invalid session
          if (error.message?.includes('session_not_found') || 
              (error as any)?.status === 403 ||
              error.message?.includes('JWT')) {
            await clearSessionAndGoHome();
            return;
          }
          
          finishProfileCheck(false);
        } else {
          // If profile exists and has first_name, consider it complete
          const isComplete = !!(data?.first_name);
          finishProfileCheck(isComplete);
          
          // Update user metadata for future quick checks
          if (isComplete) {
            await withTimeout(
              supabase.auth.updateUser({
                data: { profile_complete: true }
              }),
              AUTH_OPERATION_TIMEOUT_MS,
              'Profile update'
            );
          }
        }
      } else {
        // For new users (created recently), assume profile is not complete
        finishProfileCheck(false);
      }
    } catch (error: any) {
      console.error('Error checking profile completion:', error);
      
      if (isTimeoutError(error)) {
        console.error('Profile check timed out; continuing without blocking the page.');
        return;
      }

      // Handle refresh token errors in catch block
      if (error?.message?.includes('Refresh Token Not Found') || 
          error?.message?.includes('invalid_grant') ||
          error?.message?.includes('refresh_token_not_found')) {
        console.log('Invalid refresh token detected in profile completion check, clearing session');
        await clearSessionAndGoHome();
        return;
      }
      
      // Check if the error is due to an invalid session
      if (error?.message?.includes('session_not_found') || 
          error?.status === 403 ||
          error?.message?.includes('JWT')) {
        await clearSessionAndGoHome();
        return;
      }
      
      finishProfileCheck(false);
    } finally {
      setLoading(false);
    }
  };

  checkProfileCompleteRef.current = checkProfileComplete;

  const refreshProfileStatus = async () => {
    if (user) {
      await checkProfileComplete(user);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfileComplete(false);
    // Redirect to landing page after sign out
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, loading, profileComplete, profileCheckSettled, signOut, refreshProfileStatus }}>
      {children}
    </AuthContext.Provider>
  );
};